import React from "react";

import styles from "./styles.module.scss";
import { useQuery } from "@apollo/client";
import {
  GET_USER_BALANCE,
  GET_USER_PROFILE_LEVEL,
} from "../../api/graphql/graph";
import {
  GET_ACCOUNT_SPENDINGS,
  GET_USER_RESTRICTIONS,
} from "../../api/graphql/users";
import { ReactComponent as PlusIcon } from "../SVGcomponents/plus.svg";
import { AddFundsModal } from "../Modals/AddFundsModal";
import { Tooltip } from "../Tooltip";
import { TariffLabel, TariffLevel } from "../../sandbox/components";
import { track } from "src/config/analytics";

// Ordering of pricing tiers, used to fire `tier_upgrade` only when the user
// moves UP a tier (never on a downgrade or the initial value on mount).
const TIER_RANK: Record<string, number> = {
  Trial: 0,
  Starter: 1,
  Advanced: 2,
  Expert: 3,
  Special: 4,
};

const levelIndicatorColors = {
  Trial: "#D1D5DB",
  Starter: "#FFD646",
  Advanced: "#6172F3",
  Expert: "#06AED4",
  Special: "#06AED4",
};

const levelTextColors = {
  Trial: "#9CA3AF",
  Starter: "#846700",
  Advanced: "#4A5CE3",
  Expert: "#007D99",
  Special: "#007D99",
};


const getCurrentLevel = (totalSpendings: number, profileLevel?: string) => {
  const lowerProfileLevel = profileLevel?.toLowerCase();
  if (lowerProfileLevel === "trial") return "Trial";
  if (lowerProfileLevel === "starter") return "Starter";
  if (lowerProfileLevel === "advanced") return "Advanced";
  if (lowerProfileLevel === "pro") return "Expert";
  if (lowerProfileLevel === "custom") return "Special";

  if (totalSpendings >= 20000) return "Special";
  if (totalSpendings >= 8000) return "Expert";
  if (totalSpendings >= 2000) return "Advanced";
  return "Starter";
};

const PROFILE_LEVEL_DISPLAY_NAMES: Record<string, string> = {
  TRIAL: "Trial",
  STARTER: "Starter",
  ADVANCED: "Advanced",
  EXPERT: "Expert",
  CUSTOM: "Special",
};

const STANDARD_PRICING_LEVELS = [
  {
    name: "Trial",
    apps: 2,
    keywords: 10,
    backendLevel: "TRIAL",
  },
  {
    name: "Starter",
    apps: 5,
    keywords: 500,
    backendLevel: "STARTER",
  },
  {
    name: "Advanced",
    apps: 20,
    keywords: 1500,
    backendLevel: "ADVANCED",
  },
  {
    name: "Expert",
    apps: 50,
    keywords: 4000,
    backendLevel: "EXPERT",
  },
  {
    name: "Special",
    apps: 100,
    keywords: 10000,
    backendLevel: "CUSTOM",
  },
];

export default () => {
  const { data: balance } = useQuery(GET_USER_BALANCE);
  const { data: spendingsData } = useQuery(GET_ACCOUNT_SPENDINGS, {
    fetchPolicy: "cache-and-network",
    pollInterval: 10000,
  });
  const { data: level } = useQuery(GET_USER_PROFILE_LEVEL);
  const { data: restrictionsData } = useQuery(GET_USER_RESTRICTIONS, {
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
  });
  const [isOpened, setIsOpened] = React.useState(false);

  const totalSpendings = Math.abs(
    spendingsData?.accountLevelInfo?.totalSpendings ?? 0,
  );

  const connectedAppsCount =
    restrictionsData?.getUserRestrictions?.applications ?? 0;
  const trackedKeywordsCount =
    restrictionsData?.getUserRestrictions?.keywords ?? 0;
  const appsLimit =
    restrictionsData?.getUserRestrictions?.applicationsLimit ?? null;
  const keywordsLimit =
    restrictionsData?.getUserRestrictions?.keywordsLimit ?? null;
  const profileLevel = restrictionsData?.getUserRestrictions?.profileLevel;

  const toggleModal = () => {
    setIsOpened(!isOpened);
  };

  const balanceValue =
    balance?.getUserBalance?.balance !== undefined
      ? Number(balance.getUserBalance.balance)
      : null;
  const formattedBalance =
    balanceValue !== null
      ? balanceValue
          .toFixed(2)
          .split(".")
          .map((part, i) =>
            i === 0 ? part.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : part,
          )
      : null;

  const currentLevel = getCurrentLevel(
    totalSpendings,
    level?.user?.profileLevel?.toLowerCase(),
  );

  const currentLevelDisplayName = profileLevel
    ? PROFILE_LEVEL_DISPLAY_NAMES[profileLevel] || profileLevel
    : currentLevel;

  // balance_updated: fire whenever the displayed balance changes (top-up or
  // spend). The first observed value on mount only seeds the ref — no event.
  const prevBalanceRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (balanceValue === null) return;
    const prev = prevBalanceRef.current;
    if (prev !== null && prev !== balanceValue) {
      track("balance_updated", {
        balance: balanceValue,
        previous_balance: prev,
        delta: Number((balanceValue - prev).toFixed(2)),
      });
    }
    prevBalanceRef.current = balanceValue;
  }, [balanceValue]);

  // tier_upgrade: fire only when the user moves up to a higher tier.
  const prevTierRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const tier = currentLevelDisplayName;
    if (!tier) return;
    const prev = prevTierRef.current;
    if (
      prev &&
      prev !== tier &&
      (TIER_RANK[tier] ?? -1) > (TIER_RANK[prev] ?? -1)
    ) {
      track("tier_upgrade", { from_tier: prev, to_tier: tier });
    }
    prevTierRef.current = tier;
  }, [currentLevelDisplayName]);

  const formatUsageInfo = () => {
    if (appsLimit === null || keywordsLimit === null) return "";
    return `${connectedAppsCount}/${appsLimit} apps • ${trackedKeywordsCount}/${keywordsLimit} keywords`;
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString("en-US");
  };

  const tooltipContent = (
    <div className={styles["tooltip-levels"]}>
      {STANDARD_PRICING_LEVELS.filter((levelItem) => {
        if (levelItem.name === "Special") {
          return currentLevelDisplayName === "Special";
        }
        if (levelItem.name === "Trial") {
          return currentLevelDisplayName === "Trial";
        }
        return true;
      }).map((levelItem) => {
        const isCurrentLevel = currentLevelDisplayName === levelItem.name;
        const levelIndicatorColor =
          levelIndicatorColors[
            levelItem.name as keyof typeof levelIndicatorColors
          ] || levelIndicatorColors.Starter;
        const levelTextColor =
          levelTextColors[levelItem.name as keyof typeof levelTextColors] ||
          levelTextColors.Starter;

        const displayApps =
          isCurrentLevel && appsLimit !== null ? appsLimit : levelItem.apps;
        const displayKeywords =
          isCurrentLevel && keywordsLimit !== null
            ? keywordsLimit
            : levelItem.keywords;

        return (
          <div
            key={levelItem.name}
            className={`${styles["tooltip-level-item"]} ${isCurrentLevel ? styles["tooltip-level-item-active"] : ""}`}
          >
            <div className={styles["tooltip-level-header"]}>
              <span
                className={styles["tooltip-level-indicator"]}
                style={{
                  backgroundColor: isCurrentLevel
                    ? levelIndicatorColor
                    : "#D1D5DB",
                }}
              />
              <span
                className={styles["tooltip-level-name"]}
                style={{ color: isCurrentLevel ? levelTextColor : undefined }}
              >
                {levelItem.name}
              </span>
            </div>
            <div className={styles["tooltip-level-details"]}>
              {displayApps} {displayApps === 1 ? "app" : "apps"} •{" "}
              {formatNumber(displayKeywords)} keywords
            </div>
          </div>
        );
      })}

      {currentLevelDisplayName === "Trial" && (
        <div className={styles["tooltip-upgrade-info"]}>
          <div className={styles["tooltip-upgrade-text"]}>
            Add $ 100 to upgrade to Starter
          </div>
          {spendingsData?.accountLevelInfo?.nextCreditDate && (
            <div className={styles["tooltip-upgrade-date"]}>
              To be credited on{" "}
              {new Date(
                spendingsData.accountLevelInfo.nextCreditDate,
              ).toLocaleDateString("en-US", { day: "numeric", month: "short" })}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className={styles["header-balance-card"]}>
      {/* A phone's top bar keeps only the amount and the top-up button. */}
      <span className={styles.tariff}>
        <Tooltip
          title={tooltipContent}
          type="dropdown"
          placement="bottom"
          appendToBody={true}
        >
          <TariffLabel
            level={
              (currentLevelDisplayName || "Trial").toLowerCase() as TariffLevel
            }
            label={currentLevelDisplayName || "N/A"}
            meta={formatUsageInfo()}
          />
        </Tooltip>
      </span>
      <div className={styles["second"]}>
        {formattedBalance ? (
          <h4 className={styles["balance-heading"]}>
            <span className={styles["currency-symbol"]}>$</span>
            <span className={styles["balance-main"]}>
              {formattedBalance[0]}
            </span>
            <span className={styles["balance-cents"]}>
              .{formattedBalance[1]}
            </span>
          </h4>
        ) : (
          <h4>
            00<span>.00</span>
          </h4>
        )}
      </div>
      <div className={styles["third"]}>
        <button onClick={toggleModal}>
          <PlusIcon />
          <AddFundsModal isOpened={isOpened} closeModal={toggleModal} />
        </button>
      </div>
    </div>
  );
};
